import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ClampedLinkWorkaround } from '@hivebrite/design-system/Dotdotdot';
import { DropdownThreeDotsIcon } from '@hivebrite/design-system/Dropdown';
import { Image } from '@hivebrite/design-system/Image';
import { DefaultLink, Link } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { breakText, focusOutline } from '@hivebrite/design-system/Theme';

import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import UserSnapshot from 'hb-react/shared/components/UserSnapshot';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

import StatusBar from '../StatusBar';

const renderAvatar = (link, picture, ownerId) => (
  <AvatarWrapper>
    {link ? (
      <UserSnapshot userId={ownerId}>
        <Link aria-hidden="true" tabIndex={-1} to={link}>
          {picture}
        </Link>
      </UserSnapshot>
    ) : (
      picture
    )}
  </AvatarWrapper>
);

const Header = ({
  displayTopicName,
  landingTitle,
  titleDetailsForScreenReader,
}) => {
  const theme = useTheme();
  const {
    actionsItems,
    isActive,
    labelledById,
    owner: { name, pictureUrl, link, linkIsExternal, newPhoto, id: ownerId },
    publishedAt,
    timezone,
    topic,
  } = useLandingContext();
  const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

  const displayName = useMemo(() => {
    if (link) {
      return linkIsExternal ? (
        <UserSnapshot userId={ownerId}>
          <DefaultLink href={link} target="_blank" styledOnlyOnHover>
            {name}
          </DefaultLink>
        </UserSnapshot>
      ) : (
        <UserSnapshot userId={ownerId}>
          <Link styledOnlyOnHover to={link}>
            {name}
          </Link>
        </UserSnapshot>
      );
    }

    return name;
  }, [link, linkIsExternal, name]);
  const picture = useMemo(
    () => (
      <Image
        src={
          flagEvUserPicturesToGlobalStorage && newPhoto ? newPhoto : pictureUrl
        }
        alt=""
      />
    ),
    [pictureUrl],
  );

  return (
    <>
      {isActive && <StatusBar isActive={isActive} />}
      <Box mt={3}>
        {renderAvatar(link, picture, ownerId)}
        <Box flex={1} flexDirection="column">
          <Box flexDirection="row">
            <Box
              flex={1}
              font="h4-semibold"
              alignItems="flex-end"
              mt={{ _: 0, m: 2 }}
              id={labelledById}
              as="h3"
            >
              <Box px={2} pt={1} display="block" as="span">
                <ClampedLinkWorkaround
                  padding={3}
                  margin={-3}
                  mb={0}
                  lineHeight={theme.lineHeights[3]}
                  clamp={2}
                  tagName="span"
                >
                  <ContentWrapper font="h4-semibold">
                    {displayName}
                  </ContentWrapper>
                  {landingTitle && (
                    <>
                      {' '}
                      <TitleWrapper
                        dangerouslySetInnerHTML={{
                          __html: landingTitle.toString(),
                        }}
                      />
                    </>
                  )}
                  {titleDetailsForScreenReader && (
                    <>
                      {' '}
                      <ScreenReadersContent>
                        {titleDetailsForScreenReader}
                      </ScreenReadersContent>
                    </>
                  )}
                  {displayTopicName && (
                    <>
                      <Box as="span" font="h4-regular" display="inline">
                        {' '}
                        {s__('LiveFeed|in')}
                      </Box>
                      <ContentWrapper font="h4-semibold">
                        <Link
                          styledOnlyOnHover
                          to={Routes.topic_path(topic?.id)}
                        >
                          {' '}
                          {topic?.name}
                        </Link>
                      </ContentWrapper>
                    </>
                  )}
                </ClampedLinkWorkaround>
              </Box>
            </Box>
            {actionsItems && (
              <DropdownThreeDotsIcon
                dataTestId="landing-actions-dropdown"
                items={actionsItems}
              />
            )}
          </Box>
          <Box as="span" font="metadata-regular-asphalt" mt={1} ml={2}>
            {timezone
              ? dayjs(publishedAt).tz(timezone).fromNow()
              : dayjs(publishedAt).fromNow()}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Header;

const AvatarWrapper = styled(Box).attrs(() => ({
  height: { _: 48, m: 60 },
  mt: 1,
  mr: 1,
  width: { _: 48, m: 60 },
}))`
  border-radius: 50%;
  user-select: none;
  ${breakText}

  a {
    ${focusOutline}
    border-radius: 50%;
    height: 100%;
    width: 100%;
  }

  img {
    border-radius: 50%;
    height: 100%;
    object-fit: cover !important;
    width: 100%;
  }
`;

const ContentWrapper = styled(Box).attrs(() => ({
  display: 'inline',
  as: 'span',
}))``;

const TitleWrapper = styled(Box).attrs(() => ({
  display: 'inline',
  as: 'span',
}))`
  overflow: hidden;
`;
